import React from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { useField, useFormikContext, ErrorMessage } from "formik";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";

const FormikTextInput = ({
  name,
  label,
  max,
  min,
  maxLength,
  width = "100%",
  required = false,
  placeholder = "",
  disabled = false,
  onChange = () => {},
  readOnly = false,
  inputRestriction = "alphabetic",
  specialCharacters = false,
  ...props
}) => {
  const [field] = useField(name);
  const { setFieldValue } = useFormikContext();

  const handleChange = (e) => {
    let { value } = e.target;

    // ✅ TIME INPUT HANDLING (HH:mm)
  if (inputRestriction === "time") {
  // Allow only numbers
  value = value.replace(/[^0-9]/g, "");

  // Limit to 4 digits (HHMM)
  value = value.substring(0, 4);

  // Format only when needed
  if (value.length <= 2) {
    // Just hours (allow 98, 40, etc.)
    value = value;
  } else {
    // Add colon after 2 digits
    value = `${value.substring(0, 2)}:${value.substring(2, 4)}`;
  }
} else {
      // ✅ EXISTING RESTRICTIONS
      if (!specialCharacters) {
        if (inputRestriction === "alphanumeric") {
          value = value.replace(/[^a-zA-Z0-9\s]/g, "");
        } else if (inputRestriction === "numeric") {
          value = value.replace(/[^0-9.]/g, "");
        } else if (inputRestriction === "ifscCode") {
          value = value.replace(/[^A-Z0-9]/g, "");
        } else if (inputRestriction === "allowAll") {
          // no restriction
        } else {
          value = value.replace(/[^a-zA-Z\s]/g, "");
        }
      }

      // Max length
      if (maxLength !== undefined && value.length > maxLength) {
        value = value.substring(0, maxLength);
      }

      // Min / Max for numeric
      if (inputRestriction === "numeric") {
        const numericValue = parseInt(value || "0", 10);
        if (!isNaN(numericValue)) {
          if (min !== undefined && numericValue < min)
            value = String(min);
          if (max !== undefined && numericValue > max)
            value = String(max);
        }
      }
    }

    setFieldValue(name, value);

    onChange({
      ...e,
      target: { ...e.target, value },
    });
  };

  return (
    <div style={{ width }}>
      {label && <FormikLabel text={label} required={required} />}

      <TextField
        {...field}
        {...props}
        placeholder={placeholder}
        fullWidth
        disabled={disabled}
        inputProps={{
          readOnly,
          maxLength: inputRestriction === "time" ? 5 : maxLength, // HH:mm
        }}
        onChange={(e) => {
          if (!readOnly) {
            handleChange(e);
          }
        }}
        sx={{
          ...props.sx,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            borderWidth: "1px",
            cursor: disabled ? "not-allowed" : "inherit",
          },
          "& .MuiInputBase-input": {
            backgroundColor:
              disabled || readOnly ? "#f5f5f5" : "inherit",
            padding: "8px",
            borderRadius: "8px",
            cursor:
              disabled || readOnly ? "not-allowed" : "inherit",
          },
        }}
      />

      <div style={ErrorStyle}>
        <ErrorMessage name={name} />
      </div>
    </div>
  );
};

// ✅ PropTypes
FormikTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  inputRestriction: PropTypes.oneOf([
    "alphabetic",
    "alphanumeric",
    "numeric",
    "ifscCode",
    "allowAll",
    "time", // ✅ added
  ]),
  specialCharacters: PropTypes.bool,
};

export default FormikTextInput;